import { forwardRef, useImperativeHandle, useEffect, useRef, useState, useCallback } from "react";
import { Form } from "@bpmn-io/form-js";
import "@bpmn-io/form-js/dist/assets/form-js.css";
import "./FormRenderer.css";

const apiUrl = process.env.REACT_APP_API_URL;

const FormRenderer = forwardRef(({ schema, data, onSubmit, onChange, readOnly, externalErrors, showSubmitButton = true }, ref) => {
  const formContainerRef = useRef(null)
  const formInstanceRef = useRef(null)
  const [isFormInstanceReady, setIsFormInstanceReady] = useState(false)
  const [enrichedSchema, setEnrichedSchema] = useState(null)
  const [isLoadingExternalData, setIsLoadingExternalData] = useState(false)

  const [externalDataCache, setExternalDataCache] = useState({})
  const [autoFillSourceKeys, setAutoFillSourceKeys] = useState(new Set())
  const [dynamicUrlFields, setDynamicUrlFields] = useState([])
  const [filterFieldMap, setFilterFieldMap] = useState({})

  const isUpdatingRef = useRef(false)
  const previousValuesRef = useRef({})
  const previousFormDataRef = useRef({})
  const activeFiltersRef = useRef({})

  const fetchExternalData = async (endpoint) => {
    const finalUrl = endpoint.startsWith("http") ? endpoint : `${apiUrl}${endpoint}`

    try {
      const response = await fetch(finalUrl)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return await response.json()
    } catch (error) {
      console.error("Error fetching external data:", error)
      return []
    }
  }

  const replaceUrlPlaceholders = useCallback((url, formData) => {
    if (!url || !formData) return url

    const placeholderRegex = /\{([^}]+)\}/g

    return url.replace(placeholderRegex, (match, placeholder) => {
      const fieldKey = placeholder.replace(".value", "")
      const value = formData[fieldKey]
      return value !== undefined && value !== null ? value : ""
    })
  }, [])

  const parseFilterField = useCallback((filterFieldString) => {
    if (!filterFieldString) return null

    const match = filterFieldString.match(/externalData:([^:]+):(.+)/)

    if (!match) return null

    const targetFieldKey = match[1]
    const paramExpression = match[2]

    return {
      targetFieldKey,
      paramExpression,
    }
  }, [])

const buildUrlWithFilters = useCallback((baseUrl, targetFieldKey, formData) => {
    const filters = activeFiltersRef.current[targetFieldKey] || {};

    // 1. Separate the Base Path from the existing Query String
    const [existingBaseUrl, existingQueryString] = baseUrl.split("?");
    
    // We will modify 'finalPath' for REST style params (e.g., /123)
    let finalPath = existingBaseUrl; 
    
    // We will modify 'urlParams' for Query params (e.g., ?type=A)
    const urlParams = new URLSearchParams(existingQueryString || "");

    for (const [filterKey, paramExpression] of Object.entries(filters)) {
        const filterValue = formData[filterKey];
        const hasValue = filterValue !== null && filterValue !== undefined && filterValue !== "";

        // CHECK: Is this a Query Param (contains '=') or a Path Segment?
        if (paramExpression.includes("=")) {
            // --- LOGIC FOR QUERY PARAMS (e.g. "type={this.value}") ---
            const [paramName] = paramExpression.split("="); // Extract "type"
            
            if (hasValue && paramName) {
                urlParams.set(paramName, filterValue);
            } else if (paramName) {
                urlParams.delete(paramName);
            }
        } else {
            // --- LOGIC FOR PATH SEGMENTS (e.g. "/{this.value}") ---
            if (hasValue) {
                // Replace placeholder and encode the value to be URL-safe
                // e.g., "/{this.value}" becomes "/123"
                const pathSegment = paramExpression.replace(
                    "{this.value}", 
                    encodeURIComponent(filterValue)
                );
                
                // Append directly to the path
                // Note: This assumes existingBaseUrl doesn't end in / if pathSegment starts with /
                // You might want a check here to avoid double slashes like "//"
                finalPath += pathSegment;
            }
        }
    }

    // 2. Reconstruct the full URL
    const newQueryString = urlParams.toString();
    return newQueryString ? `${finalPath}?${newQueryString}` : finalPath;

}, []);

  const parseDependencies = useCallback((dependenciesString) => {
    if (!dependenciesString) return []

    console.log(` Parsing dependencies string: "${dependenciesString}"`)

    const matches = dependenciesString.match(/externalData:([a-zA-Z0-9_]+)/g)

    if (!matches) {
      console.log(` No matches found in dependencies string`)
      return []
    }

    const parsed = matches.map((match) => match.replace("externalData:", ""))
    console.log(` Parsed dependencies:`, parsed)
    return parsed
  }, [])

  const handleFilterFieldChange = useCallback(
    async (changedFieldKey, formData) => {
      if (isUpdatingRef.current) {
        console.log(" Already updating, skipping filter field update")
        return
      }

      const filterConfig = filterFieldMap[changedFieldKey]

      if (!filterConfig || filterConfig.length === 0) {
        return
      }

      console.log("=".repeat(60))
      console.log(` FILTER FIELD UPDATE TRIGGERED`)
      console.log(` Filter field: ${changedFieldKey}`)
      console.log(
        ` Affects targets:`,
        filterConfig.map((f) => f.targetFieldKey),
      )
      console.log("=".repeat(60))

      isUpdatingRef.current = true

      for (const filter of filterConfig) {
        const { targetFieldKey, paramExpression } = filter

        if (!activeFiltersRef.current[targetFieldKey]) {
          activeFiltersRef.current[targetFieldKey] = {}
        }

        const filterValue = formData[changedFieldKey]

        if (filterValue !== null && filterValue !== undefined && filterValue !== "") {
          activeFiltersRef.current[targetFieldKey][changedFieldKey] = paramExpression
        } else {
          delete activeFiltersRef.current[targetFieldKey][changedFieldKey]
        }

        const findFieldBaseUrl = (components) => {
          for (const component of components) {
            if (component.key === targetFieldKey) {
              return component.properties?.externalData
            }
            if (component.components) {
              const found = findFieldBaseUrl(component.components)
              if (found) return found
            }
          }
          return null
        }

        const baseUrl = findFieldBaseUrl(enrichedSchema.components)

        if (!baseUrl) {
          console.log(` X Could not find base URL for target field ${targetFieldKey}`)
          continue
        }

        const fullUrl = buildUrlWithFilters(baseUrl, targetFieldKey, formData)

        console.log(` Field ${targetFieldKey}: Base URL = ${baseUrl}`)
        console.log(` Field ${targetFieldKey}: Full URL with filters = ${fullUrl}`)

        try {
          console.log(` Field ${targetFieldKey}: Fetching filtered data`)
          const newData = await fetchExternalData(fullUrl)
          console.log(` Field ${targetFieldKey}: Fetched ${newData.length} options`)

          setExternalDataCache((prev) => ({
            ...prev,
            [targetFieldKey]: newData,
          }))

          const currentData = formInstanceRef.current._getState().data
          const currentValue = currentData[targetFieldKey]
          let updatedData = currentData

          const isValueInvalid =
            currentValue !== null && !newData.some((item) => (item.id || item.code || item.value) === currentValue)

          if (isValueInvalid) {
            updatedData = {
              ...currentData,
              [targetFieldKey]: null,
            }
            console.log(` Field ${targetFieldKey}: Existing value (${currentValue}) is invalid, setting to null.`)
          }

          const updateFieldValues = (components) => {
            for (const component of components) {
              if (component.key === targetFieldKey) {
                component.values = newData.map((item) => ({
                  value: item.id || item.code || item.value || item.provinceid || item.district,
                  label: item.name || item.label || `${item.id || item.code || item.province || item.district}`,
                }))
                return true
              }
              if (component.components) {
                if (updateFieldValues(component.components)) return true
              }
            }
            return false
          }

          updateFieldValues(enrichedSchema.components)

          await formInstanceRef.current.importSchema(enrichedSchema, updatedData)
          console.log(` Field ${targetFieldKey}: v Options updated successfully`)
        } catch (error) {
          console.error(` Field ${targetFieldKey}: X Error fetching filtered data:`, error)
        }
      }

      console.log("=".repeat(60))

      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
    },
    [enrichedSchema, filterFieldMap, buildUrlWithFilters],
  )

  const handleDynamicUrlUpdate = useCallback(
    async (changedFieldKey, formData) => {
      if (isUpdatingRef.current) {
        console.log(" Already updating, skipping to prevent loop")
        return
      }

      if (!enrichedSchema || !formInstanceRef.current) return

      const currentValue = formData[changedFieldKey]
      const previousValue = previousValuesRef.current[changedFieldKey]

      if (currentValue === previousValue && currentValue !== null) {
        console.log(` Value for ${changedFieldKey} unchanged (and not null), skipping update`)
        return
      }

      previousValuesRef.current[changedFieldKey] = currentValue
      isUpdatingRef.current = true

      console.log("=".repeat(60))
      console.log(` DYNAMIC URL UPDATE TRIGGERED`)
      console.log(` Changed field: ${changedFieldKey} (New Value: ${currentValue})`)
      console.log("=".repeat(60))

      const nextCascadeKeys = []

      const fieldsToUpdate = dynamicUrlFields.filter((field) => {
        const urlTemplate = field.urlTemplate
        const dependencies = field.dependencies || []

        const hasUrlDependency =
          urlTemplate.includes(`{${changedFieldKey}.value}`) || urlTemplate.includes(`{${changedFieldKey}}`)

        const hasDependencyDeclaration = dependencies.includes(changedFieldKey)

        return hasUrlDependency || hasDependencyDeclaration
      })

      if (fieldsToUpdate.length === 0) {
        console.log(` X No dependent fields found for ${changedFieldKey}`)
        console.log("=".repeat(60))
        isUpdatingRef.current = false
        return
      }

      console.log(
        ` v Found ${fieldsToUpdate.length} dependent field(s):`,
        fieldsToUpdate.map((f) => f.key),
      )

      for (const fieldInfo of fieldsToUpdate) {
        const { key, urlTemplate } = fieldInfo

        const resolvedUrl = replaceUrlPlaceholders(urlTemplate, formData)

        console.log(` Field ${key}: Resolved URL = ${resolvedUrl}`)

        if (resolvedUrl.includes("id=&") || (resolvedUrl.includes("id=") && resolvedUrl.endsWith("id="))) {
          console.log(` Field ${key}: URL has empty parameters, clearing options and value.`)

          setExternalDataCache((prev) => ({
            ...prev,
            [key]: [],
          }))
          const updateFieldValues = (components) => {
            for (const component of components) {
              if (component.key === key) {
                component.values = []
                return true
              }
              if (component.components) {
                if (updateFieldValues(component.components)) return true
              }
            }
            return false
          }
          updateFieldValues(enrichedSchema.components)

          const currentData = formInstanceRef.current._getState().data
          const updatedData = {
            ...currentData,
            [key]: null,
          }

          await formInstanceRef.current.importSchema(enrichedSchema, updatedData)

          nextCascadeKeys.push(key)

          continue
        }

        try {
          console.log(` Field ${key}: Fetching data from ${resolvedUrl}`)
          const newData = await fetchExternalData(resolvedUrl)
          console.log(` Field ${key}: Fetched ${newData.length} options`)

          setExternalDataCache((prev) => ({
            ...prev,
            [key]: newData,
          }))

          const currentData = formInstanceRef.current._getState().data
          const currentValue = currentData[key]
          let updatedData = currentData

          const isValueInvalid =
            currentValue !== null && !newData.some((item) => (item.id || item.code || item.value) === currentValue)

          if (isValueInvalid) {
            updatedData = {
              ...currentData,
              [key]: null,
            }
            console.log(` Field ${key}: Existing value (${currentValue}) is invalid, setting to null.`)

            nextCascadeKeys.push(key)
  
            await formInstanceRef.current.importSchema(enrichedSchema, updatedData)
            if (onChange) {
                onChange(updatedData);
            }
  
            // **Force Cascade on clear**
            nextCascadeKeys.push(key);
  
            continue
          }

          const updateFieldValues = (components) => {
            for (const component of components) {
              if (component.key === key) {
                component.values = newData.map((item) => ({
                  value: item.id || item.code || item.value,
                  label: item.name || item.label || `${item.id || item.code}`,
                }))
                return true
              }
              if (component.components) {
                if (updateFieldValues(component.components)) return true
              }
            }
            return false
          }

          updateFieldValues(enrichedSchema.components)

          await formInstanceRef.current.importSchema(enrichedSchema, updatedData)
          console.log(` Field ${key}: v Options updated successfully`)
        } catch (error) {
          console.error(` Field ${key}: X Error fetching data:`, error)
        }
      }

      for (const key of nextCascadeKeys) {
        console.log(` o Initiating cascade to next level for cleared/updated field: ${key}`)

        isUpdatingRef.current = false

        const latestFormData = formInstanceRef.current._getState().data

        await handleDynamicUrlUpdate(key, latestFormData)

        isUpdatingRef.current = true
      }

      console.log("=".repeat(60))

      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
    },
    [enrichedSchema, dynamicUrlFields, replaceUrlPlaceholders],
  )

const processSchemaWithExternalData = async (originalSchema) => {
    if (!originalSchema || !originalSchema.components) {
      return originalSchema
    }

    setIsLoadingExternalData(true)
    const processedSchema = JSON.parse(JSON.stringify(originalSchema))
    const dataCache = {}
    const sourceKeys = new Set()
    const dynamicFields = []
    const filterMap = {}

    const processComponents = async (components) => {
      for (let i = 0; i < components.length; i++) {
        const field = components[i]
        const { properties, type, key, components: nestedComponents } = field

        if (nestedComponents && Array.isArray(nestedComponents)) {
          await processComponents(nestedComponents)
        }

        if (properties && properties.filterField) {
          const filterConfig = parseFilterField(properties.filterField)

          if (filterConfig) {
            console.log(` o Field ${key} is a filter for ${filterConfig.targetFieldKey}`)
            console.log(`   Parameter: ${filterConfig.paramExpression}`)

            if (!filterMap[key]) {
              filterMap[key] = []
            }

            filterMap[key].push(filterConfig)
            sourceKeys.add(key)
          }
        }

        if (type === "select" && properties && properties.externalData) {
          sourceKeys.add(key)

          const urlTemplate = properties.externalData.replace(/{apiUrl}/g, apiUrl)
          console.log("urlTemplate", urlTemplate)
          field.properties.externalData = urlTemplate

          const dependenciesStr = properties.dependencies || properties.dependancies
          console.log(` Field ${key} - dependencies string: "${dependenciesStr}"`)

          const dependencies = parseDependencies(dependenciesStr)

          if (urlTemplate.includes("{") || dependencies.length > 0) {
            console.log(` o Field ${key} has dynamic URL: ${urlTemplate}`)
            if (dependencies.length > 0) {
              console.log(` o Field ${key} depends on: ${dependencies.join(", ")}`)
            }

            dynamicFields.push({ key, urlTemplate, dependencies })

            field.values = []
            dataCache[key] = []
          } else {
            console.log(` o Field ${key} has static URL: ${urlTemplate}`)

            try {
              const externalData = await fetchExternalData(urlTemplate)

              dataCache[key] = externalData

              field.values = externalData.map((item) => ({
                value: item.id || item.code || item.value || item.provinceid || item.district,
                label: item.name || item.label || `${item.id || item.code || item.province || item.district}`,
              }))

              console.log(` v Loaded ${field.values.length} options for field ${key}`)
            } catch (error) {
              console.error(` X Failed to load external data for field ${key}:`, error)
              field.values = []
            }
          }
        }
      }
    }

    await processComponents(processedSchema.components)

    console.log(` - Final dynamic fields array:`, dynamicFields)
    console.log(` - Filter field map:`, filterMap)

    setExternalDataCache(dataCache)
    setAutoFillSourceKeys(sourceKeys)
    setDynamicUrlFields(dynamicFields)
    setFilterFieldMap(filterMap)
    setIsLoadingExternalData(false)
    return processedSchema
  }

  const handleAutoFill = useCallback(
    (changed) => {
      const formInstance = formInstanceRef.current
      if (!formInstance || !enrichedSchema || isLoadingExternalData) return

      if (isUpdatingRef.current) {
        console.log(" Currently updating, skipping to prevent loop")
        return
      }

      console.log("--- START AUTOFILL DEBUG ---")
      console.log(" ? Full changed event:", changed)
      console.log(" ? changed.data:", changed.data)
      console.log(" ? autoFillSourceKeys:", Array.from(autoFillSourceKeys))

      let changedFieldKey = null

      if (changed.data) {
        const formState = formInstance._getState()
        const currentData = changed.data
        const previousData = previousFormDataRef.current

        console.log(" ? Previous form data:", previousData)
        console.log(" ? Current form data:", currentData)

        for (const key of autoFillSourceKeys) {
          const currentValue = currentData[key]
          const previousValue = previousData[key]

          console.log(` ? Checking key: ${key}, previous: ${previousValue}, current: ${currentValue}`)

          if (currentValue !== previousValue && currentValue !== undefined) {
            changedFieldKey = key
            console.log(` ! Field actually changed: ${changedFieldKey} (${previousValue} -> ${currentValue})`)
            break
          }
        }

        previousFormDataRef.current = { ...currentData }
      }

      if (!changedFieldKey) {
        console.log(" No auto-fill source key found/matched. Skipping logic.")
        console.log("--- END AUTOFILL DEBUG ---")
        return
      }

      const formState = formInstance._getState()

      if (filterFieldMap[changedFieldKey]) {
        console.log(` > Field ${changedFieldKey} is a filter field, calling handleFilterFieldChange`)
        handleFilterFieldChange(changedFieldKey, formState.data)
      }

      console.log(` > About to call handleDynamicUrlUpdate for field: ${changedFieldKey}`)
      handleDynamicUrlUpdate(changedFieldKey, formState.data)
      console.log(` v Returned from handleDynamicUrlUpdate`)

      const externalData = externalDataCache[changedFieldKey]
      if (!externalData) {
        console.log(` Field ${changedFieldKey} is not a data source. Skipping.`)
        console.log("--- END AUTOFILL DEBUG ---")
        return
      }

      const selectedValue = formState.data[changedFieldKey]

      console.log(` Event triggered by source key: ${changedFieldKey}. Value selected: ${selectedValue}`)

      const selectedItem = externalData.find((item) => (item.id || item.code || item.value) === selectedValue)

      if (selectedItem) {
        // console.log(` Match found for ID '${selectedValue}'. Full data object obtained:`, selectedItem);
      } else {
        console.log(` No match found for ID '${selectedValue}'. Likely cleared or invalid selection.`)
      }

      const fieldsToAutoFill = []
      const collectAutoFillFields = (components) => {
        components.forEach((component) => {
          const autoFillConfig = component.properties?.hasAutoFill
          if (autoFillConfig) {
            const parts = autoFillConfig.split(":")
            if (parts[0] === "externalData" && parts[1] === changedFieldKey && parts.length === 3) {
              fieldsToAutoFill.push({
                targetKey: component.key,
                sourceProperty: parts[2],
              })
            }
          }
          if (component.components) {
            collectAutoFillFields(component.components)
          }
        })
      }

      collectAutoFillFields(enrichedSchema.components)

      if (fieldsToAutoFill.length === 0) {
        console.log(` No target fields linked to ${changedFieldKey}. Skipping update.`)
        console.log("--- END AUTOFILL DEBUG ---")
        return
      }

      const newData = { ...formState.data }
      let updateNeeded = false
      const updateLog = []

      fieldsToAutoFill.forEach((fillConfig) => {
        const { targetKey, sourceProperty } = fillConfig

        let valueToSet = null

        if (selectedItem) {
          valueToSet = selectedItem[sourceProperty]
        }

        if (valueToSet === undefined || (typeof valueToSet === "number" && isNaN(valueToSet))) {
          valueToSet = null
        }

        updateLog.push({
          targetKey,
          sourceProperty,
          valueToSet,
          currentValue: newData[targetKey],
        })

        if (newData[targetKey] !== valueToSet) {
          newData[targetKey] = valueToSet
          updateNeeded = true
        }
      })
      console.log(newData)
      console.log(formInstance)

      if (updateNeeded) {
        isUpdatingRef.current = true

        formInstance
          .importSchema(enrichedSchema, newData)
          .then(() => {
            console.log(` SUCCESS: Form re-imported with auto-filled values`, newData)
            if (onChange) {
                onChange(newData);
            }
            setTimeout(() => {
              isUpdatingRef.current = false
            }, 100)
          })
          .catch((err) => {
            console.error(" Failed to re-import schema with new data:", err)
            isUpdatingRef.current = false
          })
      } else {
        console.log(` INFO: No actual data changes detected. Skipping form update.`)
      }
      console.log("--- END AUTOFILL DEBUG ---")
    },
    [
      enrichedSchema,
      externalDataCache,
      autoFillSourceKeys,
      isLoadingExternalData,
      handleDynamicUrlUpdate,
      filterFieldMap,
      handleFilterFieldChange,
    ],
  )

  useEffect(() => {
    if (schema) {
      processSchemaWithExternalData(schema).then(setEnrichedSchema)
    } else {
      setEnrichedSchema(null)
    }
  }, [schema, apiUrl])

  useEffect(() => {
    if (formContainerRef.current && !formInstanceRef.current) {
      console.log("FormRenderer: Container available. Attempting to create Form instance.")

      const timerId = setTimeout(() => {
        if (formContainerRef.current && !formInstanceRef.current) {
          try {
            formInstanceRef.current = new Form({
              container: formContainerRef.current,
            })
            console.log("FormRenderer: Form instance CREATED.")
            setIsFormInstanceReady(true)
          } catch (e) {
            console.error("FormRenderer: Error creating Form instance:", e)
          }
        }
      }, 0)

      return () => {
        clearTimeout(timerId)
        if (formInstanceRef.current) {
          console.log("FormRenderer: Destroying Form instance.")
          formInstanceRef.current.destroy()
          formInstanceRef.current = null
        }
        setIsFormInstanceReady(false)
      }
    }
  }, [])

  useEffect(() => {
    const formInstance = formInstanceRef.current

    if (isFormInstanceReady && formInstance) {
      formInstance.on("changed", handleAutoFill)
      console.log("FormRenderer: 'changed' listener (handleAutoFill) attached.")

      return () => {
        if (formInstance) {
          formInstance.off("changed", handleAutoFill)
          console.log("FormRenderer: 'changed' listener (handleAutoFill) detached.")
        }
      }
    }
  }, [isFormInstanceReady, handleAutoFill])

  useEffect(() => {
    if (isFormInstanceReady && formInstanceRef.current && enrichedSchema && !isLoadingExternalData) {
      console.log("FormRenderer: Importing enriched schema with initial data")

      formInstanceRef.current
        .importSchema(enrichedSchema, data || {})
        .then(() => {
          console.log("FormRenderer: Enriched schema imported successfully.")

          const formInstance = formInstanceRef.current

          if (readOnly) {
            const formFields = formInstance.get("formFields")

            const disableComponents = (components) => {
              components.forEach((component) => {
                const field = formFields.get(component.id)
                if (field && typeof field.setDisabled === "function") {
                  field.setDisabled(true)
                } else if (field && typeof field.disabled !== "undefined") {
                  field.disabled = true
                }

                if (component.components) {
                  disableComponents(component.components)
                }
              })
            }

            const schemaState = formInstance._getState()
            if (schemaState?.schema?.components) {
              disableComponents(schemaState.schema.components)
            }

            if (typeof formInstance._update === "function") {
              formInstance._update()
            } else {
              console.warn(
                "FormRenderer: formInstance._update not available. Read-only view might not render correctly.",
              )
            }
          }
        })
        .catch((err) => console.error("FormRenderer: Error importing enriched schema:", err))
    }
  }, [enrichedSchema, data, readOnly, isFormInstanceReady, isLoadingExternalData])

  const handleInternalSubmit = useCallback(
    (event) => {
      event.preventDefault()

      if (isUpdatingRef.current) {
        console.log("Submit blocked: Form is currently processing dynamic updates.")
        return
      }

      if (isFormInstanceReady && formInstanceRef.current && onSubmit) {
        try {
          const currentData = formInstanceRef.current?.get("data")
          onSubmit(currentData)
        } catch (error) {
          console.error("Error retrieving form data during submit:", error)
          if (formInstanceRef.current && formInstanceRef.current._getState) {
            const fallbackData = formInstanceRef.current._getState().data
            console.log("Attempting to submit fallback data from internal state.")
            onSubmit(fallbackData)
          }
        }
      }
    },
    [onSubmit, isFormInstanceReady],
  )

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formInstanceRef.current) {
        handleInternalSubmit(new Event('submit'));
      }
    }
  }));

  if (!schema) {
    return null
  }

  return (
    <div className="efs-form-wrapper">
      {isLoadingExternalData && (
        <div className="efs-loading-banner">
          <div className="efs-spinner"></div>
          <span>Loading reference data for the form fields...</span>
        </div>
      )}
      <form onSubmit={handleInternalSubmit} noValidate>
        <div ref={formContainerRef} className="form-js-container efs-form-card">
          {/* The form-js library will render its content here */}
        </div>
        {!readOnly && showSubmitButton && (
          <button
            type="submit"
            disabled={readOnly || !isFormInstanceReady || isLoadingExternalData || isUpdatingRef.current}
            className="efs-submit-btn"
          >
            {isLoadingExternalData || isUpdatingRef.current ? "Processing..." : "Submit Application"}
          </button>
        )}
      </form>
    </div>
  )
});

export default FormRenderer;